// ============================================================================
// ORGANISATION MASTER SCREEN — the shared skeleton for entities, business units
// and financial years of one tenant
// ============================================================================
// Tenant chosen in a selector (the Hub's tenant-scoped pattern); server-side
// paging, sorting, search and status filter through the tenant-scoped
// organisation endpoints; create / edit / view / delete and kind-specific row
// actions through the definition. Built on the enterprise shell: screen shell,
// standard confirms, action-button factory, useCan, EnterpriseFormModal.

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import {
  EnterpriseScreenShell, useStandardConfirms, actionButtonFactory, useCan, useUnsavedChangesGuard,
  type FormModalMode,
} from 'components/enterprise-shell';
import type { ActionButton } from 'components/modals';
import { SyutGrid, SyutSelect, ColumnSelector, type ActionMenuItem, type SyutGridSortState } from 'components/ui';
import TenantSelector from 'components/common/TenantSelector';
import GridLoadingSkeleton from 'components/common/GridLoadingSkeleton';
import notificationService from 'services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { useOrganisationList } from 'hooks/api/useOrganisation';
import { organisationService } from 'api/services/organisationService';
import { ORGANISATION_STATUS_OPTIONS, type OrganisationSearchParams, type OrganisationStatusCode } from 'api/types';
import { OrganisationFormModal } from './OrganisationFormModal';
import { OrganisationViewModal } from './OrganisationViewModal';
import type { OrganisationRow, OrganisationScreenDefinition } from './organisationScreen.types';

export interface OrganisationMasterScreenProps<TRecord extends OrganisationRow, TForm extends object> {
  definition: OrganisationScreenDefinition<TRecord, TForm>;
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
}

/** Status badge used by every organisation grid and view. */
export const OrganisationStatusBadge: React.FC<{ statusCode: string }> = ({ statusCode }) => {
  const active = statusCode === 'ORG_ACTIVE';
  return (
    <span className={`badge ${active ? 'badge-active' : 'badge-inactive'}`}>
      {active ? 'Active' : statusCode === 'ORG_INACTIVE' ? 'Inactive' : statusCode || '—'}
    </span>
  );
};

export function OrganisationMasterScreen<TRecord extends OrganisationRow, TForm extends object>({
  definition, moduleIcon, tabId, moduleName,
}: OrganisationMasterScreenProps<TRecord, TForm>): React.ReactElement {
  const { kind, screenCode, title, description, noun, columnDefinitions, gridColumns, recordCode, recordName, extraRowActions } = definition;
  const effectiveModuleName = moduleName || definition.moduleName;

  const { isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();
  const can = useCan(screenCode);
  const { confirmDelete, confirmCustom, ConfirmDialogPortal } = useStandardConfirms();

  // ---- Tenant selection ----
  const [tenantId, setTenantId] = useState('');

  // ---- Query (server-side paging, sorting, search, filter) ----
  const [query, setQuery] = useState<OrganisationSearchParams>({ pageNumber: 1, pageSize: 25 });
  const { data, pagination, loading, refetch } = useOrganisationList<TRecord>(kind, tenantId, query);

  const handleTenantChange = useCallback((id: string) => {
    setTenantId(id);
    setQuery(prev => ({ pageNumber: 1, pageSize: prev.pageSize }));
    setSearchQuery('');
    setStatusFilter('');
    setSorting([]);
  }, []);

  const [searchQuery, setSearchQuery] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => {
      setQuery(prev => ({ ...prev, searchTerm: searchQuery || undefined, pageNumber: 1 }));
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const [sorting, setSorting] = useState<SyutGridSortState>([]);
  const handleSortingChange = useCallback((next: SyutGridSortState) => {
    setSorting(next);
    const first = next[0];
    setQuery(prev => ({
      ...prev,
      sortBy: first?.id,
      sortDirection: first ? (first.desc ? 'desc' : 'asc') : undefined,
      pageNumber: 1,
    }));
  }, []);

  const handlePageChange = useCallback((pageIndex: number) => {
    setQuery(prev => ({ ...prev, pageNumber: pageIndex + 1 }));
  }, []);
  const handlePageSizeChange = useCallback((pageSize: number) => {
    setQuery(prev => ({ ...prev, pageSize, pageNumber: 1 }));
  }, []);

  // ---- Filter panel ----
  const [filterOpen, setFilterOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<OrganisationStatusCode | ''>('');
  useUnsavedChangesGuard({ isDirty: filterOpen && Boolean(statusFilter) });

  const applyFilters = useCallback(() => {
    setQuery(prev => ({ ...prev, statusCode: statusFilter || undefined, pageNumber: 1 }));
    setFilterOpen(false);
  }, [statusFilter]);
  const clearFilters = useCallback(() => {
    setStatusFilter('');
    setQuery(prev => ({ ...prev, statusCode: undefined, pageNumber: 1 }));
  }, []);

  // ---- Columns ----
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem(definition.columnsStorageKey);
      if (saved) return JSON.parse(saved);
    } catch { /* ignore */ }
    return columnDefinitions.filter(c => c.defaultVisible).map(c => c.id);
  });
  const handleApplyColumns = useCallback((cols: string[]) => {
    setVisibleColumns(cols);
    try { localStorage.setItem(definition.columnsStorageKey, JSON.stringify(cols)); } catch { /* ignore */ }
  }, [definition.columnsStorageKey]);
  const handleResetColumns = useCallback(() => {
    setVisibleColumns(columnDefinitions.filter(c => c.defaultVisible).map(c => c.id));
    try { localStorage.removeItem(definition.columnsStorageKey); } catch { /* ignore */ }
  }, [columnDefinitions, definition.columnsStorageKey]);

  // ---- Modals ----
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<FormModalMode>('create');
  const [viewOpen, setViewOpen] = useState(false);
  const [selected, setSelected] = useState<TRecord | null>(null);
  const [createInstanceId, setCreateInstanceId] = useState('1');

  const findByCode = useCallback((code: string) => data.find(r => recordCode(r) === code) || null, [data, recordCode]);

  const handleRestoreModal = useCallback((mode: FormModalMode, instanceId?: string, code?: string) => {
    if (mode === 'create' && instanceId) setCreateInstanceId(instanceId);
    if (code) { const rec = findByCode(code); if (rec) setSelected(rec); }
    setFormMode(mode);
    setFormOpen(true);
  }, [findByCode]);

  const handleRestoreView = useCallback((code: string) => {
    const rec = findByCode(code);
    if (rec) { setSelected(rec); setViewOpen(true); }
  }, [findByCode]);

  const restoreIfMinimized = useCallback((modalId: string | null) => {
    if (modalId && isMinimized(modalId)) {
      const modal = getMinimizedModal(modalId);
      dismissMinimized(modalId);
      modal?.onRestore?.();
      return true;
    }
    return false;
  }, [isMinimized, getMinimizedModal, dismissMinimized]);

  const handleView = useCallback((record: TRecord) => {
    if (restoreIfMinimized(tabId ? `${tabId}-view-modal-${recordCode(record)}` : null)) return;
    setSelected(record);
    setViewOpen(true);
  }, [tabId, recordCode, restoreIfMinimized]);

  const handleEdit = useCallback((record: TRecord) => {
    if (restoreIfMinimized(tabId ? `${tabId}-form-modal-edit-${recordCode(record)}` : null)) return;
    setViewOpen(false);
    setFormMode('edit');
    setSelected(record);
    setFormOpen(true);
  }, [tabId, recordCode, restoreIfMinimized]);

  const handleAdd = useCallback(() => {
    let next = 1;
    if (tabId) { while (isMinimized(`${tabId}-form-modal-create-${next}`)) next++; }
    setCreateInstanceId(String(next));
    setFormMode('create');
    setSelected(null);
    setFormOpen(true);
  }, [tabId, isMinimized]);

  const handleDelete = useCallback(async (record: TRecord) => {
    const ok = await confirmDelete({
      resourceLabel: `${recordCode(record)} - ${recordName(record)}`,
      message: `"${recordName(record)}" will be removed from the organisation master. Applications that project it will see it as inactive.`,
    });
    if (!ok) return;
    try {
      const res = await organisationService.remove(kind, tenantId, record.id);
      if (!res.success) throw new Error(res.message);
      notificationService.toast.success(`${noun} "${recordName(record)}" deleted.`);
      refetch();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || err?.message || 'Delete failed.');
    }
  }, [confirmDelete, recordCode, recordName, kind, tenantId, noun, refetch]);

  const handleSaved = useCallback(() => {
    setFormOpen(false);
    setSelected(null);
    refetch();
  }, [refetch]);

  // ---- Row actions ----
  const getRowActions = useCallback((record: TRecord): ActionMenuItem[] => {
    const items: ActionMenuItem[] = [
      { id: 'view', label: 'View Details', icon: <IconSystem.eye size={18} />, onClick: () => handleView(record) },
    ];
    if (can('UPDATE')) {
      items.push({ id: 'edit', label: 'Edit', icon: <IconSystem.edit size={18} />, onClick: () => handleEdit(record) });
    }
    if (extraRowActions) {
      items.push(...extraRowActions(record, { tenantId, refetch, confirm: confirmCustom, canManage: can('UPDATE') }));
    }
    if (can('DELETE')) {
      items.push({ id: 'delete', label: 'Delete', icon: <IconSystem.trash size={18} />, onClick: () => handleDelete(record), variant: 'danger' });
    }
    return items;
  }, [can, handleView, handleEdit, handleDelete, extraRowActions, tenantId, refetch, confirmCustom]);

  const actionButtons: ActionButton[] = [
    { ...actionButtonFactory.add(handleAdd, `Add ${noun}`), visible: can('CREATE'), disabled: !tenantId },
    actionButtonFactory.refresh(refetch),
  ];

  const headerIcon = useMemo(() => {
    const name = moduleIcon || definition.defaultIcon;
    const Icon = (IconSystem as any)[name];
    return Icon ? React.createElement(Icon, { size: 18 }) : undefined;
  }, [moduleIcon, definition.defaultIcon]);

  const gridData = useMemo(() => data as Array<TRecord & { id: string }>, [data]);

  return (
    <EnterpriseScreenShell
      title={title}
      description={description}
      icon={headerIcon}
      tabId={tabId}
      actionButtons={actionButtons}
      searchConfig={{ value: searchQuery, onChange: setSearchQuery, placeholder: definition.searchPlaceholder }}
      filterConfig={{
        onClick: () => setFilterOpen(!filterOpen),
        isActive: filterOpen,
        appliedCount: statusFilter ? 1 : 0,
        disabled: !tenantId,
      }}
      customToolbarItems={
        <ColumnSelector
          columns={columnDefinitions}
          visibleColumns={visibleColumns}
          onApply={handleApplyColumns}
          onReset={handleResetColumns}
        />
      }
      trailingContent={filterOpen && (
        <div className="search-panel">
          <div className="search-panel-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
              <IconSystem.filter size={14} />
              <h3 className="search-panel-title">Filters</h3>
            </div>
            <button onClick={() => setFilterOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem', display: 'flex', alignItems: 'center', color: 'var(--text-muted)' }} title="Close filters" type="button">
              <IconSystem.x size={16} />
            </button>
          </div>
          <div className="search-panel-content">
            <div className="filter-section">
              <SyutSelect
                label="Status"
                options={ORGANISATION_STATUS_OPTIONS}
                value={statusFilter}
                onChange={(val) => setStatusFilter(((val as string) || '') as OrganisationStatusCode | '')}
                placeholder="All statuses"
                isClearable
                size="sm"
              />
            </div>
          </div>
          <div className="filter-actions">
            <button className="btn btn-secondary" onClick={clearFilters} type="button">Clear</button>
            <button className="btn btn-primary" onClick={applyFilters} type="button">Apply</button>
          </div>
        </div>
      )}
      ConfirmDialogPortal={ConfirmDialogPortal}
    >
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0.75rem', padding: '0 0.25rem', flexShrink: 0 }}>
        <div style={{ width: '360px', maxWidth: '100%' }}>
          <TenantSelector value={tenantId} onChange={handleTenantChange} />
        </div>
        {tenantId && (
          <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', paddingBottom: '0.5rem' }}>
            {`${pagination.totalCount} ${pagination.totalCount === 1 ? noun.toLowerCase() : title.toLowerCase()}`}
          </div>
        )}
      </div>

      <div className="data-grid-container">
        {!tenantId ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)' }}>
            <IconSystem.home size={40} style={{ marginBottom: '0.75rem', opacity: 0.4 }} />
            <p>Select a tenant to manage its {noun.toLowerCase()} records.</p>
          </div>
        ) : loading && data.length === 0 ? (
          <GridLoadingSkeleton rows={10} columns={visibleColumns.length || 6} />
        ) : (
          <SyutGrid<TRecord & { id: string }>
            data={gridData}
            columns={gridColumns as any}
            isLoading={loading}
            enablePagination
            manualPagination
            totalCount={pagination.totalCount}
            currentPage={(query.pageNumber ?? 1) - 1}
            pageSize={query.pageSize ?? 25}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            manualSorting
            sorting={sorting}
            onSortingChange={handleSortingChange}
            rowActions={getRowActions}
            visibleColumns={visibleColumns}
            emptyMessage={`No ${noun.toLowerCase()} records found`}
          />
        )}
      </div>

      <OrganisationFormModal<TRecord, TForm>
        definition={definition}
        tenantId={tenantId}
        isOpen={formOpen}
        mode={formMode}
        record={selected}
        tabId={tabId}
        moduleName={effectiveModuleName}
        createInstanceId={createInstanceId}
        onClose={() => { setFormOpen(false); setSelected(null); }}
        onSaved={handleSaved}
        onRestoreModal={handleRestoreModal}
      />

      <OrganisationViewModal<TRecord, TForm>
        definition={definition}
        isOpen={viewOpen}
        record={selected}
        canEdit={can('UPDATE')}
        onClose={() => { setViewOpen(false); setSelected(null); }}
        onEdit={handleEdit}
        tabId={tabId}
        moduleName={effectiveModuleName}
        onRestoreView={handleRestoreView}
      />
    </EnterpriseScreenShell>
  );
}

export default OrganisationMasterScreen;
